import React, { useEffect, useState } from 'react';
import { FileText, Loader2, Pencil } from 'lucide-react';
import { archiveApi } from '../mocks/archiveApi';
import { ArchiveDoc, CONTRACT_ROWS, ContractFields } from '../types/archive';

const show = (type: string, v: string | number | null) =>
  v === null || v === '' ? '—' : type === 'won' ? `₩${Number(v).toLocaleString()}` : String(v);

/**
 * 검수 칸 — 계약 값을 원본과 맞대 보고 고친다.
 *
 * 값 옆의 `p.N` 은 추출할 때 그 값을 읽은 쪽이다. 누르면 원본 PDF 를 그 쪽으로 연다
 * (서버가 드라이브에서 읽기 전용으로 받아 넘긴다). **저장하면 검수로 표시된다** — 사람이 고친
 * 값은 다시 추출해도 덮이지 않는다.
 */
export const ArchiveContractSection: React.FC<{ doc: ArchiveDoc; onSaved: () => Promise<void>; canOpen: boolean }> = ({ doc, onSaved, canOpen }) => {
  const c = doc.contract!;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ContractFields>(c);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 다른 문서를 고르면 편집을 접는다
  useEffect(() => { setEditing(false); setDraft(doc.contract!); setError(null); }, [doc.id, doc.contract]);

  const openPage = async (page: number) => {
    // 창은 클릭 순간에 연다 — 받아 온 뒤에 열면 팝업 차단에 걸린다
    const w = window.open('', '_blank');
    setBusy(`p${page}`);
    try {
      const url = URL.createObjectURL(await archiveApi.file(doc.id));
      if (w) w.location.href = `${url}#page=${page}`;
    } catch (e) {
      w?.close();
      setError(`원본을 열지 못했습니다 — ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    // 바뀐 칸만 보낸다 — 보내지 않은 칸은 서버가 건드리지 않는다
    const patch: Partial<ContractFields> = {};
    for (const r of CONTRACT_ROWS) {
      if (draft[r.key] !== c[r.key]) (patch as Record<string, unknown>)[r.key] = draft[r.key] === '' ? null : draft[r.key];
    }
    if (!Object.keys(patch).length) { setEditing(false); return; }
    setBusy('save');
    setError(null);
    try {
      await archiveApi.updateContract(doc.id, patch);
      await onSaved();
      setEditing(false);
    } catch (e) {
      setError(`저장하지 못했습니다 — ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  const input = 'w-full px-1.5 py-0.5 text-xs border border-slate-200 rounded focus:border-indigo-400 focus:outline-none';

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-slate-500">계약 값</span>
        {!editing && (
          <button onClick={() => setEditing(true)} className="flex items-center gap-1 text-indigo-600 hover:underline">
            <Pencil size={11} /> 수정
          </button>
        )}
      </div>
      {CONTRACT_ROWS.map((r) => {
        const page = canOpen && doc.isPdf ? doc.evidence?.[r.evidence] : undefined;
        return (
          <div key={r.key} className="flex items-center gap-2">
            <span className="w-16 flex-shrink-0 text-slate-500">{r.label}</span>
            <span className="flex-1 min-w-0">
              {!editing ? (
                <span className="font-medium text-slate-900 break-words">{show(r.type, c[r.key])}</span>
              ) : r.type === 'vat' ? (
                <select value={draft.vat ?? ''} onChange={(e) => setDraft({ ...draft, vat: (e.target.value || null) as ContractFields['vat'] })} className={input}>
                  <option value="">—</option>
                  {['포함', '별도', '미기재'].map((v) => <option key={v}>{v}</option>)}
                </select>
              ) : (
                <input
                  type={r.type === 'date' ? 'date' : r.type === 'won' ? 'number' : 'text'}
                  value={draft[r.key] ?? ''}
                  onChange={(e) => setDraft({
                    ...draft,
                    [r.key]: r.type === 'won' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value,
                  })}
                  className={`${input} ${r.type === 'won' ? 'text-right tabular-nums' : ''}`}
                />
              )}
            </span>
            {page ? (
              <button
                onClick={() => openPage(page)}
                disabled={busy !== null}
                title={`원본 ${page}쪽 열기`}
                className="flex-shrink-0 flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] text-indigo-600 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-40"
              >
                {busy === `p${page}` ? <Loader2 size={10} className="animate-spin" /> : <FileText size={10} />}p.{page}
              </button>
            ) : <span className="w-8 flex-shrink-0" />}
          </div>
        );
      })}
      {editing && (
        <div className="flex justify-end gap-1.5 pt-1">
          <button onClick={() => { setEditing(false); setDraft(c); }} disabled={busy === 'save'}
            className="px-2.5 py-1 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50">
            취소
          </button>
          <button onClick={save} disabled={busy === 'save'}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40">
            {busy === 'save' && <Loader2 size={11} className="animate-spin" />} 저장 · 검수 완료
          </button>
        </div>
      )}
      {error && <p className="text-red-600">{error}</p>}
    </div>
  );
};
